import clsx from "clsx";
import { Category } from "services-hooks/types";
import styles from "./country-list.module.scss";
import Image from "next/image";
import MemoLogo from "../icons/Logo";
import ArrowRight from "../icons/ArrowRight";
import { useRef } from "react";
import { useGetPillowCategoryPath } from "services-hooks/hooks";
import { useFeaturesContext } from "app/context/features.context";
import { calculateCountryListScroll } from "app/utils/scroll";

export interface CountryListProps {
  regionPermalink: string;
  label?: string;
  showDetailsOnClick?: boolean;
  wrapped?: boolean;
  rows?: number;
  className?: string;
  showNames?: boolean;
  onSelect: (country: Category) => void;
}

export const CountryList: React.FC<CountryListProps> = ({
  regionPermalink,
  label,
  wrapped,
  className,
  showNames = false,
  onSelect,
}) => {
  const radioListRef = useRef<HTMLDivElement>(null);
  const { arrowNavigationEnabled } = useFeaturesContext();
  const { data, isLoading, fetchNextPage } = useGetPillowCategoryPath(
    regionPermalink,
    {
      pageSize: 100,
    }
  );
  const countryList =
    data?.pages.reduce((countryList, countryPage) => {
      return [...countryList, ...(countryPage.data.body?.content || [])];
    }, [] as Category[]) || [];
  const onForward = () => {
    radioListRef.current?.scrollTo({
      left: calculateCountryListScroll(radioListRef.current.scrollLeft),
      behavior: "smooth",
    });
  };
  const onBackward = () => {
    radioListRef.current?.scrollTo({
      left: calculateCountryListScroll(radioListRef.current.scrollLeft, true),
      behavior: "smooth",
    });
  };
  return (
    <div className={clsx(styles.wrapper, className)}>
      <div className={styles.header}>
        {<h2>{label}</h2>}
        {!arrowNavigationEnabled && !wrapped && (
          <div className={styles.buttons}>
            <button
              className={clsx(styles.arrow, styles.arrow_back)}
              onClick={() => onBackward()}
            >
              <ArrowRight />
            </button>
            <button className={styles.arrow} onClick={() => onForward()}>
              <ArrowRight />
            </button>
          </div>
        )}
      </div>

      <div className={styles.radio_list} ref={radioListRef}>
        {isLoading ? (
          <div className={styles.row}>
            <div
              className={clsx(styles.radio_station, styles.placeholder_item)}
            />
            <div
              className={clsx(styles.radio_station, styles.placeholder_item)}
            />
            <div
              className={clsx(styles.radio_station, styles.placeholder_item)}
            />
            <div
              className={clsx(styles.radio_station, styles.placeholder_item)}
            />
            <div
              className={clsx(styles.radio_station, styles.placeholder_item)}
            />
            <div
              className={clsx(styles.radio_station, styles.placeholder_item)}
            />
            <div
              className={clsx(styles.radio_station, styles.placeholder_item)}
            />
            <div
              className={clsx(styles.radio_station, styles.placeholder_item)}
            />
          </div>
        ) : (
          <div
            className={clsx(styles.row, {
              [styles.wrapped]: wrapped,
            })}
            data-navigate-row
            data-navigate-scrollable
            data-navigate-scrollable-parent
          >
            {countryList.map((country, index) => (
              <div
                data-navigate-item
                tabIndex={0}
                key={`country-${index}`}
                className={clsx(styles.radio_station, {
                  [styles.show_station_names]: showNames,
                })}
                onClick={() => onSelect(country)}
              >
                {country?.logo ? (
                    <Image
                      src={country?.logo || ""}
                      layout="intrinsic"
                      width={102}
                      className={styles.cover}
                      height={102}
                      objectFit="cover"
                      alt=""
                      unoptimized
                    />
                ) : (
                  <div className={clsx(styles.cover, styles.no_image)}>
                    <MemoLogo />
                  </div>
                )}
                {showNames && (
                  <div className={styles.station_title}>{country.name}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
